import { Injectable } from "@angular/core";
import { UtilsService } from "../_utils.service";
import { SubjectLocation, SubjectModel } from "../../_objects/subject-model";
import { InspectionReport } from "../../_objects/inspection-report";
import { SpinnerService } from "../spinner.service";
import { InspectionReportService } from "../http-api/inspection-report.service";
import { CommonSnackBarService } from "../common-snack-bar.service";
import { TranslateService } from "@ngx-translate/core";
import { firstValueFrom } from "rxjs";
import { InspectionImageListItem } from "../../_objects/inspection-image";
import { InspectionFileValidator } from "../image-upload/inspection-file.validator";

@Injectable({
  providedIn: "root"
})
export class LocationManagementService {
  constructor(
    private utilsService: UtilsService,
    private oscSpinnerService: SpinnerService,
    private inspectionReportService: InspectionReportService,
    private snackBar: CommonSnackBarService,
    private translate: TranslateService
  ) {}

  isSelectedLocationForReport(location: SubjectLocation, report: InspectionReport): boolean {
    return report.reportLocations.some((loc) => loc.identifier === location.identifier);
  }

  isSelectedAllTerminateLocation4Report(location: SubjectLocation, report: InspectionReport, model: SubjectModel): boolean {
    if (!location || !report || !model) {
      return false;
    }
    const termLocations = this.getTerminateLocations(model, location) || [];
    return termLocations.every((termLoc) => report.reportLocations.some((repLoc) => repLoc.identifier === termLoc.identifier));
  }

  isIndeterminatePrimaryLocation4Report(location: SubjectLocation, report: InspectionReport, model: SubjectModel): boolean {
    const termLocations = this.getTerminateLocations(model, location) || [];
    let foundInReport = false,
      notFoundInReport = false;
    termLocations.forEach((loc) => {
      if (report.reportLocations.some((repLoc) => repLoc.identifier === loc.identifier)) {
        foundInReport = true;
      } else {
        notFoundInReport = true;
      }
    });

    return foundInReport && notFoundInReport;
  }

  getTerminateLocations(subjectModel: SubjectModel, filterLocation: SubjectLocation): SubjectLocation[] {
    return this.utilsService.getLocations(subjectModel, filterLocation);
  }

  getPrimaryLocation(termLocIdentifier: string, subjectModel: SubjectModel): SubjectLocation | null {
    if (!termLocIdentifier || !subjectModel) {
      return null;
    }

    return subjectModel.locations.find((loc) => this.isIncludeTermLoc(termLocIdentifier, loc)) || null;
  }

  getTerminateLocation(termLocIdentifier: string, subjectModel: SubjectModel): SubjectLocation | null {
    if (!termLocIdentifier || !subjectModel) {
      return null;
    }

    return this.utilsService.getLocations(subjectModel).find((loc) => termLocIdentifier === loc.identifier) || null;
  }

  toggleLocationInReport(
    location: SubjectLocation,
    inspectionReport: InspectionReport,
    inspectionImages: InspectionImageListItem[] = []
  ): InspectionReport {
    const isSelected = this.isSelectedLocationForReport(location, inspectionReport);

    if (isSelected) {
      inspectionReport.reportLocations = inspectionReport.reportLocations.filter((repLoc) => repLoc.identifier !== location.identifier);
    } else {
      const imageIds = inspectionImages
        .filter(
          (image) => image.subjectLocationId === location.identifier && InspectionFileValidator.isValidImageFileType(image.mimeType, image.fileName)
        )
        .map((image) => image.id);
      inspectionReport.reportLocations.push({ identifier: location.identifier, imageIds: imageIds });
    }

    return inspectionReport;
  }
  selectPrimaryLocationForReport(primLoc: SubjectLocation, inspectionReport: InspectionReport, subjectModel: SubjectModel): void {
    const terminateLocations = this.utilsService.getLocations(subjectModel, primLoc);

    if (!this.isSelectedAllTerminateLocation4Report(primLoc, inspectionReport, subjectModel)) {
      // Add all terminate locations
      terminateLocations.forEach((loc) => {
        if (!this.isSelectedLocationForReport(loc, inspectionReport)) {
          inspectionReport = this.toggleLocationInReport(loc, inspectionReport);
        }
      });
    } else {
      // Remove all terminate locations
      terminateLocations.forEach((loc) => {
        if (this.isSelectedLocationForReport(loc, inspectionReport)) {
          inspectionReport = this.toggleLocationInReport(loc, inspectionReport);
        }
      });
    }

    // Patch the report after modifying the locations
    this.patchInspectionReportLocations(inspectionReport);
  }

  async patchInspectionReportLocations(inspectionReport: InspectionReport): Promise<void> {
    try {
      this.oscSpinnerService.show();

      const data = await firstValueFrom(
        this.inspectionReportService.patchInspectionReportLocations(
          this.inspectionReportService.convertInspectionReport2InspectionReportUpdateLocations(inspectionReport)
        )
      );

      inspectionReport.reportLocations = data.reportLocations;
    } catch (error) {
      console.log(error);
    } finally {
      this.oscSpinnerService.hide();
    }
  }

  private isIncludeTermLoc(termLocIdentifier: string, targetLoc: SubjectLocation): boolean {
    if (targetLoc.identifier === termLocIdentifier) {
      return true;
    } else {
      let result: boolean;
      for (const child of targetLoc.children) {
        result = this.isIncludeTermLoc(termLocIdentifier, child);
        if (result) {
          break;
        }
      }
      return result;
    }
  }
}
